import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Button, Alert, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';

import InfoRow from 'components/common/InfoRow';
import ManageProductEditForm from 'components/add_product/manage-product/ManageProductDetail/ManageProductEditForm';
import ManageProductViewMode from 'components/add_product/manage-product/ManageProductDetail/ManageProductViewMode';

import { productApi } from 'api';

import useFetchById from 'hooks/useFetchById';
import useUpdate from 'hooks/useUpdate';

import { isValidValue, parseFormValue } from 'utils/valueFormatters';
import { TIME_OPTION } from 'constants/time';

const ManageProductDetail = () => {
  const { productId } = useParams();

  const [isEditing, setIsEditing] = useState(false);
  const [taxTypes, setTaxTypes] = useState([]);

  const {
    data: product,
    loading: fetchLoading,
    error: fetchError,
    setData: setProduct,
  } = useFetchById(productApi, productId);

  const {
    update,
    loading: updateLoading,
    error: updateError,
  } = useUpdate(productApi);

  const handleEditClick = async () => {
    try {
      const response = await productApi.getTaxTypes();

      setTaxTypes(response);
      setIsEditing(true);
    } catch (err) {
      toast.error('Failed to fetch tax types');
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    try {
      const formData = new FormData(e.target);
      const updateData = {};

      const fieldTypes = {
        name: 'string',
        barcode: 'string',
        scan_type: 'string',
        hsn_code: 'string',
        is_active: 'boolean',
        length: 'number',
        width: 'number',
        height: 'number',
        tax_type_id: 'number',
      };

      Object.entries(fieldTypes).forEach(([field, type]) => {
        const value = formData.get(field);
        if (isValidValue(value)) {
          updateData[field] = parseFormValue(value, type);
        }
      });

      const weightValue = parseFormValue(
        formData.get('weight_value'),
        'number',
      );
      const weightUnit = formData.get('weight_unit');

      if (weightValue !== undefined || weightUnit) {
        updateData.weight = {
          ...(weightValue !== undefined && { value: weightValue }),
          ...(weightUnit && { unit: weightUnit }),
        };
      }

      const mrpAmount = parseFormValue(formData.get('mrp_amount'), 'number');
      if (mrpAmount !== undefined) {
        updateData.mrp = {
          amount: mrpAmount,
          currency: product.mrp.currency,
        };
      }

      const response = await update(productId, updateData);
      setProduct(response);
      setIsEditing(false);
      toast.success('Product updated successfully');
    } catch (err) {
      toast.error('Failed to update product');
    }
  };

  if (fetchLoading) {
    return (
      <Card className="h-100">
        <Card.Body>
          <div className="text-center">Loading...</div>
        </Card.Body>
      </Card>
    );
  }

  if (fetchError) {
    return (
      <Card className="h-100">
        <Card.Body>
          <Alert variant="danger">{fetchError}</Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <Card className="mb-3">
        <Card.Header className="bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Product Detail</h5>
            {!isEditing && (
              <Button variant="primary" size="sm" onClick={handleEditClick}>
                <FontAwesomeIcon icon="edit" className="me-2" />
                Edit
              </Button>
            )}
          </div>
        </Card.Header>

        <Card.Body>
          {updateError && <Alert variant="danger">{updateError}</Alert>}

          {isEditing ? (
            <ManageProductEditForm
              product={product}
              taxTypes={taxTypes}
              onSubmit={handleSubmit}
              onCancel={() => setIsEditing(false)}
              isLoading={updateLoading}
            />
          ) : (
            <ManageProductViewMode product={product} />
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Header className="bg-light">
          <h5 className="mb-0">Additional Information</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <InfoRow label="SKU" value={product?.sku} />

              <InfoRow label="Brand Name" value={product?.brand?.name} />
            </Col>

            <Col md={6}>
              <InfoRow label="MRP Currency" value={product?.mrp?.currency} />

              <InfoRow
                label="Created Date"
                value={new Date(product?.created).toLocaleString(
                  'en-IN',
                  TIME_OPTION,
                )}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </>
  );
};

export default ManageProductDetail;
